import { createPortal } from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import {
  BUILD_REVISION,
  captureUpdateEntryValues,
  fetchDeployedRevision,
  isUpdateReloadBlocked,
  RELOAD_REQUEST_KEY,
  shortRevision,
  UPDATE_APPLIED_KEY,
  UPDATE_DEFERRED_KEY,
} from '../../lib/pwaUpdate'
import { requestServiceWorkerUpdate } from '../../lib/serviceWorkerUpdate'

function readAppliedRevision() {
  try {
    const revision = window.sessionStorage.getItem(UPDATE_APPLIED_KEY)
    if (revision === BUILD_REVISION) {
      window.sessionStorage.removeItem(UPDATE_APPLIED_KEY)
      return revision
    }
  } catch {
    // Storage can be unavailable in private browsing; the update still works.
  }
  return ''
}

function readDeferred() {
  try { return window.localStorage.getItem(UPDATE_DEFERRED_KEY) === BUILD_REVISION } catch { return false }
}

export default function PwaUpdateControl() {
  const [waitingWorker, setWaitingWorker] = useState(null)
  const [availableRevision, setAvailableRevision] = useState('')
  const [reloadBlocked, setReloadBlocked] = useState(false)
  const [applying, setApplying] = useState(false)
  const [deferred, setDeferred] = useState(readDeferred)
  const [readyToReload, setReadyToReload] = useState(false)
  const [updateError, setUpdateError] = useState('')
  const [appliedRevision, setAppliedRevision] = useState(readAppliedRevision)
  const reloadRequested = useRef('')
  const reloadTriggered = useRef(false)

  useEffect(() => {
    if (!applying) return undefined
    const timer = window.setTimeout(() => {
      reloadRequested.current = ''
      setApplying(false)
      setUpdateError('The update is taking longer than expected. You can try again or continue using the app.')
      try { window.sessionStorage.removeItem(RELOAD_REQUEST_KEY) } catch {}
    }, 15000)
    return () => window.clearTimeout(timer)
  }, [applying])

  useEffect(() => {
    if (!appliedRevision) return undefined
    const timer = window.setTimeout(() => setAppliedRevision(''), 8000)
    return () => window.clearTimeout(timer)
  }, [appliedRevision])

  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return undefined

    let disposed = false
    let registration = null
    let installingWorker = null
    let hadController = Boolean(navigator.serviceWorker.controller)

    const refreshReloadBlocked = () => {
      if (!disposed) setReloadBlocked(isUpdateReloadBlocked())
    }

    const readLatestRevision = async () => {
      try {
        const revision = await fetchDeployedRevision()
        if (!disposed) setAvailableRevision(revision)
      } catch (error) {
        console.warn('PWA version check failed', error)
      }
    }

    const surfaceWaitingWorker = () => {
      if (disposed) return
      const worker = registration?.waiting
      if (!worker || !navigator.serviceWorker.controller) return
      setWaitingWorker(worker)
      refreshReloadBlocked()
      readLatestRevision()
    }

    const onInstallingStateChange = () => {
      if (installingWorker?.state === 'installed') {
        window.setTimeout(surfaceWaitingWorker, 0)
      }
    }

    const onUpdateFound = () => {
      if (installingWorker) {
        installingWorker.removeEventListener('statechange', onInstallingStateChange)
      }
      installingWorker = registration?.installing
      installingWorker?.addEventListener('statechange', onInstallingStateChange)
    }

    const checkForUpdate = async () => {
      if (!registration) return
      try {
        await registration.update()
        surfaceWaitingWorker()
      } catch (error) {
        console.warn('PWA update check failed', error)
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate()
    }

    const onControllerChange = () => {
      const replacedController = hadController
      hadController = Boolean(navigator.serviceWorker.controller)
      // clients.claim on a first install is not an update to this page.
      if (!replacedController || !hadController) return
      setWaitingWorker(null)
      setReadyToReload(true)
      setUpdateError('')
      setApplying(false)
      refreshReloadBlocked()
      if (!reloadRequested.current || reloadTriggered.current) return

      // An entry may have opened while the worker was activating. Never reload it.
      if (isUpdateReloadBlocked()) {
        reloadRequested.current = ''
        try { window.sessionStorage.removeItem(RELOAD_REQUEST_KEY) } catch {}
        return
      }
      reloadTriggered.current = true
      try {
        window.sessionStorage.removeItem(RELOAD_REQUEST_KEY)
        window.sessionStorage.setItem(UPDATE_APPLIED_KEY, reloadRequested.current)
      } catch {
        // The in-memory guard still guarantees one reload for this page.
      }
      window.location.reload()
    }

    const onServiceWorkerMessage = event => {
      if (event.data?.type !== 'FOS_SW_CACHE_PURGED') return
      window.__FOS_LAST_SW_SHELL_SOURCE__ = `service_worker_purged:${event.data.revision}`
    }

    // focusout precedes the browser's activeElement update. Read it after blur.
    const onFocusOut = () => queueMicrotask(refreshReloadBlocked)
    captureUpdateEntryValues()
    const observer = new MutationObserver(() => {
      captureUpdateEntryValues()
      refreshReloadBlocked()
    })
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-update-blocking', 'data-update-entry'] })
    document.addEventListener('input', refreshReloadBlocked, true)
    document.addEventListener('change', refreshReloadBlocked, true)
    document.addEventListener('focusin', refreshReloadBlocked, true)
    document.addEventListener('focusout', onFocusOut, true)
    document.addEventListener('visibilitychange', onVisibilityChange)
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
    navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage)

    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      .then(nextRegistration => {
        if (disposed) return
        registration = nextRegistration
        registration.addEventListener('updatefound', onUpdateFound)
        surfaceWaitingWorker()
        checkForUpdate()
      })
      .catch(error => console.warn('Service worker registration failed', error))

    return () => {
      disposed = true
      observer.disconnect()
      document.removeEventListener('input', refreshReloadBlocked, true)
      document.removeEventListener('change', refreshReloadBlocked, true)
      document.removeEventListener('focusin', refreshReloadBlocked, true)
      document.removeEventListener('focusout', onFocusOut, true)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
      navigator.serviceWorker.removeEventListener('message', onServiceWorkerMessage)
      registration?.removeEventListener('updatefound', onUpdateFound)
      installingWorker?.removeEventListener('statechange', onInstallingStateChange)
    }
  }, [])

  const applyUpdate = () => {
    if ((!waitingWorker && !readyToReload) || applying || reloadTriggered.current) return
    const blocked = isUpdateReloadBlocked()
    setReloadBlocked(blocked)
    if (blocked) return

    setUpdateError('')
    const revision = availableRevision || `unknown-${Date.now()}`
    reloadRequested.current = revision
    setApplying(true)
    try {
      window.sessionStorage.setItem(RELOAD_REQUEST_KEY, revision)
    } catch {
      // The in-memory request is sufficient for this page lifetime.
    }
    if (readyToReload) {
      reloadTriggered.current = true
      try {
        window.sessionStorage.removeItem(RELOAD_REQUEST_KEY)
        window.sessionStorage.setItem(UPDATE_APPLIED_KEY, revision)
      } catch {}
      window.location.reload()
      return
    }
    try {
      requestServiceWorkerUpdate(waitingWorker)
    } catch {
      reloadRequested.current = ''
      setApplying(false)
      setUpdateError('The update could not start. Try again or continue using the app.')
      try { window.sessionStorage.removeItem(RELOAD_REQUEST_KEY) } catch {}
    }
  }

  const deferUpdate = () => {
    reloadRequested.current = ''
    setApplying(false)
    setDeferred(true)
    try { window.sessionStorage.removeItem(RELOAD_REQUEST_KEY) } catch {}
    try { window.localStorage.setItem(UPDATE_DEFERRED_KEY, BUILD_REVISION) } catch {}
  }

  if (!waitingWorker && !readyToReload && !appliedRevision) return null

  if (appliedRevision) {
    return createPortal(
      <aside
        className="fixed inset-x-3 app-update-notice z-30 mx-auto max-w-md rounded-xl border border-sage/40 bg-charcoal-700/95 px-4 py-3 text-center text-sm text-sage shadow-2xl backdrop-blur"
        role="status"
      >
        Updated to build {shortRevision(appliedRevision)}.
      </aside>, document.body,
    )
  }

  if (deferred) return createPortal(
    <button type="button" onClick={() => setDeferred(false)}
      className="fixed right-3 app-update-notice z-30 min-h-[44px] rounded-full border border-gold/40 bg-charcoal-700 px-4 text-xs text-cream shadow-lg">
      App update
    </button>, document.body,
  )
  return <PendingUpdateNotice reloadBlocked={reloadBlocked} applying={applying} onApply={applyUpdate} onDefer={deferUpdate} error={updateError} />
}

// Update notices belong below modal entry surfaces: a blocked reload must
// never cover the very confirmation the user needs to finish first.
export function PendingUpdateNotice({ reloadBlocked, applying = false, onApply, onDefer, error = '' }) {
  return createPortal(
    <aside
      className="fixed inset-x-3 app-update-notice z-30 mx-auto max-w-md rounded-xl border border-gold/50 bg-charcoal-700/95 p-4 shadow-2xl backdrop-blur"
      aria-live="polite"
    >
      <p className="text-sm font-semibold text-cream">Update available</p>
      <p className="mt-1 text-xs leading-relaxed text-cream/55">
        {reloadBlocked
          ? 'Finish or close your current entry, then reload safely.'
          : 'Reload now to use the latest FragranceOS build.'}
      </p>
      {error && <p role="status" className="mt-2 text-xs text-cream/70">{error}</p>}
      <div className="mt-3 flex gap-2">
      {onDefer && <button type="button" onClick={onDefer} className="min-h-[44px] rounded-lg border border-cream/20 px-4 text-sm text-cream">Later</button>}
      <button
        type="button"
        onClick={onApply}
        disabled={reloadBlocked || applying}
        className="min-h-[44px] flex-1 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-charcoal-900 transition-colors hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-40"
      >
        {applying ? 'Updating…' : reloadBlocked ? 'Finish entry first' : 'Reload now'}
      </button>
      </div>
    </aside>, document.body,
  )
}
